import type { AccountingObjectType } from 'core/accounting/domain/AccountingObjectType'
import type { PriceItemType } from 'core/accounting/domain/PriceItemType'
import {
  mapApiToPriceItemType,
  type RawPriceItemType,
} from 'core/accounting/infra/PriceItemTypeMapper'
import type { FullMode } from 'core/domain/Mode'
import type { Stop, StopType } from 'core/domain/Stop'

import type {
  ShipmentAccountingSummary,
  ShipmentChargeStatus,
  ShipmentChargeEventType,
  ShipmentChargeAction,
  ShipmentChargeSource,
  ShipmentCharge,
  ShipmentChargeAttachment,
  ShipmentChargeType,
  ShipmentChargeEvent,
  NewShipmentCharge,
} from '../domain/ShipmentCharges'
import { noStopOption } from '../ui/hooks/useShipmentChargeStopOptions'

export type RawShipmentAccountingSummary = {
  quoted_total: number
  pending_charges: number
  approved_charges: number
  total: number
}

export type RawShipmentChargeType = {
  uuid: string
  name: string
  code: string
  is_accessorial: boolean
  usage: Array<AccountingObjectType>
  mode: Array<FullMode>
  external_reference: string | null
  stop_relationship: Array<StopType>
  created_at: string
  deleted_at: string | null
}

export type RawShipmentChargeEvent = {
  uuid: string
  type: ShipmentChargeEventType
  user: {
    id: number
    username: string
  } | null
  created_at: string
  description: string | null
}

export type RawShipmentChargeAttachment = {
  id?: number
  file_name?: string
  file_url: string
  created_at?: string
}

export type RawShipmentChargeStop = {
  id: number
  type: 'pickup' | 'delivery'
  stop_index: number
  date: string
  address: string
  city: string
  state: string
  country: string
  zipcode: string
  contact: {
    uuid: string
    name: string
    email: string
    phone_number: string
    phone_number_extension: string | null
  } | null
  date_source: string | null
  facility: {
    company_name: string
  } | null
}

export type RawShipmentChargeCarrier = {
  id: number
  uuid: string
  name: string
  dot: string
  mc: string | null
  company_name: string | null
}

export type RawShipmentCharge = {
  uuid: string
  shipment_charge_type: RawShipmentChargeType | null
  price_item_type: RawPriceItemType | null
  shipment_stop: RawShipmentChargeStop | null
  carrier: RawShipmentChargeCarrier
  status: { value: ShipmentChargeStatus; label: string }
  unit: string
  quantity: number
  amount_per_unit: string
  total: number
  notes: string | null
  shipment_charge_events: Array<RawShipmentChargeEvent>
  source: ShipmentChargeSource
  updated_at: string | null
  available_actions: Array<ShipmentChargeAction>
  shipment_charge_attachment: RawShipmentChargeAttachment | null
}

export type NewShipmentChargePayload = {
  status: ShipmentChargeStatus
  charge_type?: Pick<ShipmentChargeType, 'code'>
  price_item_type?: Pick<PriceItemType, 'uuid'>
  source: ShipmentChargeSource
  shipment_stop?: { stop_index: number }
  quantity: number
  rate: number
  notes?: string
}

export function mapApiToShipmentAccountingSummary(
  summary: RawShipmentAccountingSummary
): ShipmentAccountingSummary | undefined {
  if (!summary || !Object.keys(summary).length) {
    return undefined
  }
  return {
    quotedTotal: summary.quoted_total,
    pendingCharges: summary.pending_charges,
    approvedCharges: summary.approved_charges,
    total: summary.total,
  }
}

function getChargeEvent(
  chargeEvents: Array<RawShipmentChargeEvent>,
  type: ShipmentChargeEventType
) {
  return chargeEvents.find(event => event.type === type)
}

function mapApiToStop(stop: RawShipmentChargeStop | null): Stop | undefined {
  if (!stop) {
    return undefined
  }
  return {
    facilityName: stop.facility?.company_name,
    address: stop.address,
    city: stop.city,
    state: stop.state,
    country: stop.country,
  }
}

function mapApiToChargeAttachment(
  attachment: RawShipmentChargeAttachment | null
): ShipmentChargeAttachment | undefined {
  if (!attachment?.file_url) {
    return undefined
  }
  return {
    fileName: attachment.file_name || 'Attachment',
    fileUrl: attachment.file_url,
  }
}

export function mapApiToShipmentChargeType(chargeType: RawShipmentChargeType): ShipmentChargeType {
  return {
    uuid: chargeType.uuid,
    code: chargeType.code,
    name: chargeType.name,
    isAccessorial: chargeType.is_accessorial,
    stopRelationship: chargeType.stop_relationship,
  }
}

export function mapApiToShipmentChargeEvent(
  charge: RawShipmentCharge,
  chargeEvent: RawShipmentChargeEvent | undefined
): ShipmentChargeEvent | undefined {
  if (!chargeEvent) {
    return undefined
  }

  let performedBy = undefined

  if (chargeEvent.user?.username) {
    performedBy = chargeEvent.user.username
  } else if (charge.source === 'carrier') {
    performedBy = charge.carrier.name ?? charge.carrier.company_name ?? 'Carrier'
  } else if (charge.source === 'quote') {
    performedBy = 'From quote'
  } else {
    performedBy = 'Shipper'
  }

  return {
    createdAt: chargeEvent.created_at,
    performedBy: performedBy,
    description: chargeEvent.description,
  }
}

export function mapApiToShipmentCharge(charge: RawShipmentCharge): ShipmentCharge {
  const chargeAttachment = mapApiToChargeAttachment(charge.shipment_charge_attachment)
  const chargeType = charge.shipment_charge_type
    ? mapApiToShipmentChargeType(charge.shipment_charge_type)
    : undefined
  const priceItemType = charge.price_item_type
    ? mapApiToPriceItemType(charge.price_item_type)
    : undefined
  const createdEvent = mapApiToShipmentChargeEvent(
    charge,
    getChargeEvent(charge.shipment_charge_events, 'creation')
  )
  const canceledEvent = mapApiToShipmentChargeEvent(
    charge,
    getChargeEvent(charge.shipment_charge_events, 'cancellation')
  )
  const approvedEvent = mapApiToShipmentChargeEvent(
    charge,
    getChargeEvent(charge.shipment_charge_events, 'approval')
  )
  const rejectedEvent = mapApiToShipmentChargeEvent(
    charge,
    getChargeEvent(charge.shipment_charge_events, 'rejection')
  )
  const canBeCanceled = charge.available_actions.includes('cancel')
  const stop = mapApiToStop(charge.shipment_stop)

  return {
    uuid: charge.uuid,
    type: chargeType,
    priceItemType,
    createdAt: createdEvent?.createdAt,
    createdBy: createdEvent?.performedBy,
    canceledBy: canceledEvent?.performedBy,
    approvedBy: approvedEvent?.performedBy,
    rejectedBy: rejectedEvent?.performedBy,
    status: charge.status,
    quantity: charge.quantity,
    unitPrice: Number(charge.amount_per_unit) || 0,
    total: charge.total,
    stop: stop,
    attachment: chargeAttachment,
    description: charge.notes ?? '',
    rejectReason: rejectedEvent?.description ?? undefined,
    cancelReason: canceledEvent?.description ?? undefined,
    canBeCanceled: canBeCanceled,
  }
}

export function mapNewShipmentChargeToApi(newCharge: NewShipmentCharge): NewShipmentChargePayload {
  return {
    status: 'pending',
    charge_type: newCharge.chargeType ? { code: newCharge.chargeType.value } : undefined,
    price_item_type: newCharge.priceItemType ? { uuid: newCharge.priceItemType.value } : undefined,
    source: 'carrier',
    shipment_stop:
      newCharge.stop!.value !== noStopOption.value
        ? { stop_index: newCharge.stop!.value as number }
        : undefined,
    quantity: newCharge.quantity || 1,
    rate: newCharge.unitPrice!,
    notes: newCharge.description,
  }
}
